import { supabase } from '../../supabase';
import { isNetworkError, isOnline } from './network';
import { enqueueMutation } from './outbox';
import { newClientOpId, newRowId } from './ids';
import {
  mergeOptimisticDelete,
  mergeOptimisticInsert,
  mergeOptimisticUpdate,
} from './optimisticCache';

export function shouldQueueWrites() {
  return !isOnline();
}

export function applyMatch(query, match = {}) {
  let next = query;
  Object.entries(match).forEach(([key, value]) => {
    if (value !== undefined) next = next.eq(key, value);
  });
  return next;
}

export async function queueInsert(table, payload, { preserveColumns = [], source = 'generic' } = {}) {
  const row = {
    ...payload,
    id: payload.id || newRowId(),
    client_op_id: payload.client_op_id || newClientOpId(),
  };
  const item = await enqueueMutation({
    table,
    op: 'insert',
    payload: row,
    preserveColumns,
    source,
    clientOpId: row.client_op_id,
  });
  mergeOptimisticInsert(table, { ...row, __offline: true, __outboxId: item.id });
  return { data: { ...row, __offline: true }, error: null, queued: true };
}

export async function queueUpdate(table, payload, match, { source = 'generic' } = {}) {
  const item = await enqueueMutation({
    table,
    op: 'update',
    payload,
    match,
    source,
    clientOpId: newClientOpId(),
  });
  mergeOptimisticUpdate(table, match, payload);
  return { data: { ...payload, ...(match || {}), __offline: true, __outboxId: item.id }, error: null, queued: true };
}

export async function queueDelete(table, match, { source = 'generic' } = {}) {
  await enqueueMutation({
    table,
    op: 'delete',
    payload: {},
    match,
    source,
    clientOpId: newClientOpId(),
  });
  mergeOptimisticDelete(table, match);
  return { data: match, error: null, queued: true };
}

function stripClientOnlyFields(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  const { client_op_id, __offline, __outboxId, __insertedHidden, ...rest } = payload;
  return rest;
}

export async function executeGenericMutation(item) {
  const table = item.table;
  if (item.op === 'insert') {
    const { data, error } = await supabase.from(table).insert(stripClientOnlyFields(item.payload)).select('*');
    if (error) return { data: null, error };
    return { data: Array.isArray(data) ? data[0] : data, error: null };
  }
  if (item.op === 'update') {
    const { data, error } = await applyMatch(supabase.from(table).update(item.payload), item.match).select('*');
    if (error) return { data: null, error };
    return { data: Array.isArray(data) ? data[0] : data, error: null };
  }
  if (item.op === 'delete') {
    const { error } = await applyMatch(supabase.from(table).delete(), item.match);
    return { data: item.match, error };
  }
  return { data: null, error: new Error(`Unknown outbox op: ${item.op}`) };
}

export async function runGenericMutation({
  table,
  op,
  payload = {},
  match = null,
  select = '*',
  source = 'generic',
}) {
  const queueInsertPayload = async (body) => {
    if (Array.isArray(body)) {
      const queued = [];
      for (const row of body) {
        queued.push(await queueInsert(table, row, { source }));
      }
      return {
        data: queued.map((item) => item.data),
        error: queued.find((item) => item.error)?.error || null,
        queued: true,
      };
    }
    return queueInsert(table, body, { source });
  };

  if (shouldQueueWrites()) {
    if (op === 'insert') return queueInsertPayload(payload);
    if (op === 'update') return queueUpdate(table, payload, match, { source });
    return queueDelete(table, match, { source });
  }

  try {
    if (op === 'insert') {
      const { data, error } = await supabase.from(table).insert(payload).select(select);
      if (error && isNetworkError(error)) return queueInsertPayload(payload);
      return { data: Array.isArray(payload) ? data : (Array.isArray(data) ? data[0] : data), error };
    }
    if (op === 'update') {
      const { data, error } = await applyMatch(supabase.from(table).update(payload), match).select(select);
      if (error && isNetworkError(error)) return queueUpdate(table, payload, match, { source });
      return { data: Array.isArray(data) ? data[0] : data, error };
    }
    const { error } = await applyMatch(supabase.from(table).delete(), match);
    if (error && isNetworkError(error)) return queueDelete(table, match, { source });
    return { data: match, error };
  } catch (error) {
    if (isNetworkError(error)) {
      if (op === 'insert') return queueInsert(table, payload, { source });
      if (op === 'update') return queueUpdate(table, payload, match, { source });
      return queueDelete(table, match, { source });
    }
    throw error;
  }
}
